/**
 * Geolocation — one high-accuracy fix, on demand.
 * @file src/web/shared/hooks/useGeolocation.ts
 *
 * @llm-rule WHEN: A clock-in or clock-out needs the device position
 * @llm-rule AVOID: watchPosition for attendance — a single fix at the moment of
 *   the action is what gets recorded, and a watch drains battery for nothing
 */

import { useCallback, useEffect, useState } from 'react';

export interface Fix {
  latitude: number;
  longitude: number;
  accuracyM: number;
}

export type GeoState = 'idle' | 'locating' | 'ready' | 'denied' | 'unavailable';

export interface UseGeolocationResult {
  fix: Fix | null;
  state: GeoState;
  error: string | null;
  locate: () => Promise<Fix | null>;
}

/** 15s is long enough for a cold GPS fix without stranding the user. */
const TIMEOUT_MS = 15_000;

export function useGeolocation(autoLocate = true): UseGeolocationResult {
  const [fix, setFix] = useState<Fix | null>(null);
  const [state, setState] = useState<GeoState>('idle');
  const [error, setError] = useState<string | null>(null);

  const locate = useCallback(async (): Promise<Fix | null> => {
    if (!navigator.geolocation) {
      setState('unavailable');
      setError('This device cannot report its location.');
      return null;
    }

    setState('locating');
    setError(null);

    return new Promise<Fix | null>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const next: Fix = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracyM: position.coords.accuracy,
          };
          setFix(next);
          setState('ready');
          resolve(next);
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) {
            setState('denied');
            setError('Location access was blocked. Attendance needs it to confirm where you are.');
          } else if (err.code === err.TIMEOUT) {
            setState('unavailable');
            setError('Getting a location fix timed out. Move somewhere with a clearer signal.');
          } else {
            setState('unavailable');
            setError('Your location could not be determined.');
          }
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: TIMEOUT_MS, maximumAge: 0 },
      );
    });
  }, []);

  useEffect(() => {
    if (autoLocate) void locate();
  }, [autoLocate, locate]);

  return { fix, state, error, locate };
}
